import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormErrorMessage,
  HStack,
  Spinner,
  Text,
  Textarea,
  VStack,
  Wrap,
  WrapItem,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  useAIDraft,
  useCreateAIChatSession,
  useExportAIDraft,
  useRestoredAIChatSession,
  useSendAIChatMessageStream,
} from '@/lib/sdk/ai-chat/queries';
import { extractAIError, isChatEndedError } from '@/lib/sdk/ai-chat/types';
import type { AIChatMessage, AIClarifyingQuestion, AIDraftExport } from '@/lib/sdk/ai-chat/types';
import { DraftCard } from './DraftCard';

const messageSchema = z.object({
  message: z.string().trim().min(1, 'Describe the workflow or the change').max(8000, 'Keep it under 8000 characters'),
});

type MessageFormData = z.infer<typeof messageSchema>;

interface Exchange {
  role: 'user' | 'assistant';
  content: string;
  questions?: AIClarifyingQuestion[];
}

interface AIBuildPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** The workflow being edited; absent when creating one */
  targetChatbotId?: string;
  /** Loads a ready draft's save request into the editor */
  onOpenInEditor: (draftExport: AIDraftExport) => void;
  /** Pre-fills the first message, such as a Solve plan's build brief */
  initialMessage?: string;
}

function exchangesFrom(messages: AIChatMessage[]): Exchange[] {
  return messages
    .filter((message) => message.role !== 'system')
    .map((message) => ({ role: message.role as Exchange['role'], content: message.content, questions: message.metadata?.questions }));
}

/**
 * Build with AI: a conversation that builds a draft, the draft card with what the user
 * must create or confirm, and Open in editor once the draft is ready. The assistant
 * never saves; the user reviews the draft in the editor and saves it there. The session
 * stays in the URL (?build=), so a reload returns to it instead of starting another.
 */
export function AIBuildPanel({ isOpen, onClose, targetChatbotId, onOpenInEditor, initialMessage }: AIBuildPanelProps) {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [restoreId] = useState(searchParams.get('build') ?? '');
  const [sessionId, setSessionId] = useState<string | null>(restoreId || null);
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [progress, setProgress] = useState<string | null>(null);
  const restored = useRestoredAIChatSession(
    restoreId,
    (messages) => setExchanges(exchangesFrom(messages)),
    (session) =>
      (session.session_type === 'workflow_create' || session.session_type === 'workflow_modify') &&
      session.target_chatbot_id === targetChatbotId
  );
  const createSession = useCreateAIChatSession();
  const sendMessage = useSendAIChatMessageStream();
  const exportDraft = useExportAIDraft();
  const { data: draftView } = useAIDraft(restored.isForeign && sessionId === restoreId ? null : sessionId);
  const userBg = useColorModeValue('brand.50', 'gray.700');
  const isWorking = createSession.isPending || sendMessage.isPending || restored.isLoading || restored.isResuming;

  useEffect(() => {
    if (restored.problem) {
      toast({ status: 'error', position: 'top', title: restored.problem });
    }
  }, [restored.problem, toast]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<MessageFormData>({
    resolver: zodResolver(messageSchema),
    defaultValues: { message: restoreId ? '' : (initialMessage ?? '') },
  });

  /** The session to use, created on the first message */
  const ensureSession = async (): Promise<string> => {
    // A session that could not be restored (deleted or expired) is replaced
    const current = restored.isGone && sessionId === restoreId ? null : sessionId;
    if (current) {
      return current;
    }
    // A new session can't see an ended chat's history, so it leaves the screen too
    if (restored.isGone && restoreId) {
      setExchanges((previous) => previous.slice(-1));
      toast({ status: 'info', position: 'top', title: 'That chat has ended, so this starts a new one' });
    }
    const created = (
      await createSession.mutateAsync(
        targetChatbotId ? { session_type: 'workflow_modify', target_chatbot_id: targetChatbotId } : { session_type: 'workflow_create' }
      )
    ).id;
    setSessionId(created);
    setSearchParams(
      (params) => {
        params.set('ai', '1');
        params.set('build', created);
        return params;
      },
      { replace: true }
    );
    return created;
  };

  const onSubmit = async ({ message }: MessageFormData) => {
    setExchanges((previous) => [...previous, { role: 'user', content: message }]);
    // Not reset(): that brings back a Solve brief, the form's default
    reset({ message: '' });
    try {
      const response = await sendMessage.mutateAsync({
        sessionId: await ensureSession(),
        data: { content: message },
        handlers: { onProgress: setProgress },
      });
      const answer = response.message;
      setExchanges((previous) => [
        ...previous,
        { role: 'assistant', content: answer?.content ?? '', questions: answer?.metadata?.questions },
      ]);
    } catch (error) {
      // Back in the box, ready to send again: a Solve brief is long to retype
      if (isChatEndedError(error)) {
        setSessionId(null);
        setExchanges([]);
      } else {
        setExchanges((previous) => previous.slice(0, -1));
      }
      setValue('message', message);
      toast({
        status: 'error',
        position: 'top', // the default covers Send
        title: 'The assistant could not reply',
        description: extractAIError(error)?.message ?? (error instanceof Error ? error.message : undefined),
      });
    } finally {
      setProgress(null);
    }
  };

  const answerWith = (option: string) => {
    const current = getValues('message').trim();
    setValue('message', current ? `${current}\n${option}` : option);
  };

  const openInEditor = async () => {
    if (!sessionId) return;
    try {
      const result = await exportDraft.mutateAsync(sessionId);
      if (!result.ready) {
        toast({ status: 'warning', title: 'The draft is not ready yet', description: 'Resolve what the draft card lists, then try again.' });
        return;
      }
      onOpenInEditor(result.export);
      onClose();
    } catch (error) {
      toast({ status: 'error', title: 'The draft could not be opened', description: error instanceof Error ? error.message : undefined });
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent data-testid="ai-build-panel">
        <DrawerCloseButton />
        <DrawerHeader>
          Build with AI
          <Text fontSize="xs" fontWeight="normal" color="text-secondary">
            {targetChatbotId
              ? 'Describe the change. The draft opens in the editor for you to review and save.'
              : 'Describe the workflow. The draft opens in the editor for you to review and save.'}
          </Text>
        </DrawerHeader>

        <DrawerBody>
          <VStack align="stretch" spacing={3}>
            {exchanges.map((exchange, index) =>
              exchange.role === 'user' ? (
                <Box key={index} bg={userBg} borderRadius="md" px={3} py={2} alignSelf="flex-end" data-testid="ai-build-user-message">
                  <Text fontSize="sm" whiteSpace="pre-wrap">
                    {exchange.content}
                  </Text>
                </Box>
              ) : (
                <Box key={index} data-testid="ai-build-assistant-message">
                  <Text fontSize="sm" whiteSpace="pre-wrap">
                    {exchange.content}
                  </Text>
                  {exchange.questions?.map((question) => (
                    <Box key={question.text} mt={2}>
                      <Text fontSize="sm" fontWeight="medium">
                        {question.text}
                      </Text>
                      <Wrap spacing={1} mt={1}>
                        {question.options?.map((option) => (
                          <WrapItem key={option}>
                            <Button size="xs" variant="outline" onClick={() => answerWith(option)} data-testid="ai-build-option">
                              {option}
                            </Button>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </Box>
                  ))}
                </Box>
              )
            )}
            {isWorking && (
              <HStack spacing={2} data-testid="ai-build-progress">
                <Spinner size="xs" />
                <Text fontSize="xs" color="text-secondary">
                  {progress ?? (restored.isResuming ? 'Still working…' : 'Working…')}
                </Text>
              </HStack>
            )}
            {sessionId && draftView?.draft && (
              <DraftCard sessionId={sessionId} view={draftView} onOpenInEditor={openInEditor} isOpening={exportDraft.isPending} />
            )}
          </VStack>
        </DrawerBody>

        <DrawerFooter>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
            <FormControl isInvalid={!!errors.message}>
              <Textarea
                size="sm"
                rows={3}
                placeholder={targetChatbotId ? 'e.g. Add a photo of the shelf after the stock count' : 'e.g. A store audit form: pick the store, count stock, photo of the shelf'}
                isDisabled={isWorking}
                data-testid="ai-build-input"
                {...register('message')}
              />
              <FormErrorMessage>{errors.message?.message}</FormErrorMessage>
            </FormControl>
            <Button type="submit" size="sm" mt={2} isLoading={isWorking} data-testid="ai-build-send-btn">
              Send
            </Button>
          </form>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
